import React, { useRef, useEffect, useState } from 'react';
import './ScratchAndWin.css';
import Swal from 'sweetalert2';
import { v4 as uuid } from 'uuid';

import { ScratchWinProps } from '../../app/models/scratchWinProps';
import { FormStateProps } from '../../app/models/formStateProps';
import { ModalConfig } from '../../app/models/modalConfig';
import { Player } from '../../app/models/player';
import { ScratchPrize } from '../../app/models/scratchPrize';
import { ScreenStyle } from '../../app/models/screenStyle';

import UserDataModal, { UserDataValues } from '../UserDataModal';
import agent from '../../app/api/agent';
import { toast } from 'react-toastify';
import { env } from '../../app/utils/env';
import { getWinningPrize } from '../../app/utils/getWinningPrize';

interface ScratchAndWinProps {
  isActive?: boolean;
  collectUserData: number;
  scratchWin: ScratchWinProps['scratchWin'];
  scratchPrizes?: ScratchWinProps['scratchPrizes'];
  screenStyle: ScreenStyle;
  formState: FormStateProps['formState'];
  campaignId: string;
  onStockDepletion: () => void;
  modalConfig: ModalConfig;
  winProbability?: number; // 0-1 arası, varsayılan 0.5
  scratchThreshold?: number; // %70 varsayılan
}

const ScratchAndWin: React.FC<ScratchAndWinProps> = ({
  isActive,
  collectUserData,
  scratchWin,
  scratchPrizes = [],
  screenStyle,
  formState,
  campaignId,
  onStockDepletion,
  modalConfig,
  winProbability = 0.5,
  scratchThreshold = 70
}) => {
  const scratchCanvasRef = useRef<HTMLCanvasElement>(null);
  const resultCanvasRef = useRef<HTMLCanvasElement>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const revealingRef = useRef(false);
  const submittingRef = useRef(false);
  const awaitingRefreshRef = useRef(false);
  
  const [isGameActive, setIsGameActive] = useState<boolean>(true);
  const [scratchedPercentage, setScratchedPercentage] = useState<number>(0);

  const [prizeNumber, setPrizeNumber] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const [player, setPlayer] = useState<Player | null>(null);
  const [prizesState, setPrizesState] = useState(scratchPrizes);

  const initialFormValues: any = {
    firstName: "",
    lastName: "",
    email: formState.formEmail ? "" : undefined,
    phone: formState.formPhone ? "" : undefined,
    company: formState.formCompany ? "" : undefined,
    gender: formState.formGender ? 1 : undefined,
    formAddPermissionChecked: false,
    formAddPermission2Checked: false,
    ...(formState.bonusField && { [formState.bonusField]: "" }),
  };
  const [formValues, setFormValues] = useState(initialFormValues);

  const [dataCollected, setDataCollected] = useState(false);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(true);

  const resolveAssetUrl = (value?: string) =>
    value ? (value.startsWith('http') ? value : env + value) : '';

  const logoUrl = resolveAssetUrl(screenStyle.logo);
  const coverImageUrl = resolveAssetUrl(scratchWin.coverImage);
  const titleText = screenStyle.pageTitle || 'Kazı Kazan Oyunu';
  const mottoText = screenStyle.motto || 'Şansını dene! Aşağıdaki alanı kazı ve hediyeni keşfet!';
  const textColor = screenStyle.textColor || '#ffffff';

  const nonNullPrizes = prizesState.filter(p => p.probability != null);
  const areAllProbabilitiesZero = nonNullPrizes.length > 0 && nonNullPrizes.every(p => p.probability === 0);

  useEffect(() => {
    if (!isActive || areAllProbabilitiesZero) {
      Swal.fire({
        title: "Oyun Bitti",
        text: "Oyun sona erdi. Katıldığınız için teşekkürler.",
        icon: "warning",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showCloseButton: false,
        showConfirmButton: false,
      });
  
      setIsGameActive(false);
    }
  }, [prizesState, areAllProbabilitiesZero]);

  useEffect(() => {
    // sync local list to the latest from backend (includes redistributed probabilities)
    setPrizesState(scratchPrizes ?? []);

    // if we were waiting for backend to push new probabilities, start next round now
    if (awaitingRefreshRef.current) {
      awaitingRefreshRef.current = false;
      void newGame();
    }
  }, [scratchPrizes]);

  // Draw result layer (image or fallback gradient+text)
  const drawResultLayer = async (prize: ScratchPrize | null) => {
    if (!prize) return;

    const resultCanvas = resultCanvasRef.current;
    const ctx = resultCanvas?.getContext('2d');
    if (!resultCanvas || !ctx) return;

    const W = 400;
    const H = 200;
    resultCanvas.width = W;
    resultCanvas.height = H;

    ctx.clearRect(0, 0, W, H);

    // Prefer scratchImage (relative or absolute)
    if (prize.scratchImage) {
      const src = prize.scratchImage.startsWith('http') ? prize.scratchImage : env + prize.scratchImage;
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          // cover
          const ratio = Math.max(W / img.width, H / img.height);
          const nw = img.width * ratio;
          const nh = img.height * ratio;
          const dx = (W - nw) / 2;
          const dy = (H - nh) / 2;
          ctx.drawImage(img, dx, dy, nw, nh);
          resolve();
        };
        img.onerror = () => {
          resolve(); // fallback to text if image fails
        };
        img.src = src;
      });
    } else {
      // Fallback: gradient + text
      const grad = ctx.createLinearGradient(0, 0, W, H);
      if (prize.isPrize) {
        grad.addColorStop(0, '#f1c40f');
        grad.addColorStop(1, '#f39c12');
      } else {
        grad.addColorStop(0, '#95a5a6');
        grad.addColorStop(1, '#7f8c8d');
      }
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      ctx.fillStyle = '#2c3e50';
      ctx.textAlign = 'center';
      ctx.font = 'bold 22px Arial';
      ctx.fillText(prize.prizeValue || (prize.isPrize ? 'Tebrikler!' : 'Tekrar Deneyiniz'), W / 2, H / 2);
    }
  };

  const drawScratchLayer = () => {
    const scratchCanvas = scratchCanvasRef.current;
    const ctx = scratchCanvas?.getContext('2d');
    if (!scratchCanvas || !ctx) return;

    const W = 400, H = 200;
    scratchCanvas.width = W;
    scratchCanvas.height = H;

    ctx.clearRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'source-over';

    if (coverImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ratio = Math.max(W / img.width, H / img.height);
        const nw = img.width * ratio;
        const nh = img.height * ratio;
        const dx = (W - nw) / 2;
        const dy = (H - nh) / 2;
        ctx.drawImage(img, dx, dy, nw, nh);
      };
      img.onerror = () => {
        const scratchGradient = ctx.createLinearGradient(0, 0, W, H);
        scratchGradient.addColorStop(0, '#bdc3c7');
        scratchGradient.addColorStop(1, '#95a5a6');
        ctx.fillStyle = scratchGradient;
        ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#34495e';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('KAZI', W / 2, H / 2 - 10);
        ctx.fillText('KAZAN', W / 2, H / 2 + 20);
      };
      img.src = coverImageUrl;
      return;
    }

    const scratchGradient = ctx.createLinearGradient(0, 0, W, H);
    scratchGradient.addColorStop(0, '#bdc3c7');
    scratchGradient.addColorStop(1, '#95a5a6');
    ctx.fillStyle = scratchGradient;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = '#34495e';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('KAZI', W / 2, H / 2 - 10);
    ctx.fillText('KAZAN', W / 2, H / 2 + 20);
  };

  const initGame = async () => {
    revealingRef.current = false;
    submittingRef.current = false;

    if (collectUserData === 2 && !dataCollected) {
      setOpenModal(true);
      setIsGameActive(false);
      return;
    }
    if (!isActive || areAllProbabilitiesZero) {
      setIsGameActive(false);
      return;
    }

    const toProb = (v: number | string | null | undefined) =>
      typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v)
      : typeof v === 'string' ? Math.max(0, parseFloat(v.replace(',', '.')))
      : 0;

    const eligible = prizesState.filter(
      p => !p.isPrize || ((p.stock ?? 0) > 0 && toProb(p.probability) > 0)
    );
    const winnerPrize = getWinningPrize(eligible) ?? null;

    if (!winnerPrize) {
      setPrizeNumber(-1);
      setIsGameActive(false);
      Swal.fire({ title: "Ödül bulunamadı", text: "Lütfen daha sonra tekrar deneyiniz.", icon: "info" });
      return;
    }

    const idx = prizesState.findIndex(p => p.id === winnerPrize.id);
    setPrizeNumber(idx);

    // Draw layers using the object from *state* to avoid drift
    const winnerFromState = prizesState[idx];
    await drawResultLayer(winnerFromState);
    drawScratchLayer();

    setScratchedPercentage(0);
    setIsGameActive(true);
  };

  const scratch = (e: MouseEvent | React.MouseEvent) => {
    if (!isGameActive) return;

    const scratchCanvas = scratchCanvasRef.current;
    const ctx = scratchCanvas?.getContext('2d');
    if (!scratchCanvas || !ctx) return;

    const rect = scratchCanvas.getBoundingClientRect();
    const x = (e as MouseEvent).clientX - rect.left;
    const y = (e as MouseEvent).clientY - rect.top;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, 2 * Math.PI);
    ctx.fill();

    calculateScratchedArea();
  };

  const calculateScratchedArea = () => {
    const scratchCanvas = scratchCanvasRef.current;
    const ctx = scratchCanvas?.getContext('2d');
    if (!scratchCanvas || !ctx) return;

    const W = scratchCanvas.width;
    const H = scratchCanvas.height;
    const imageData = ctx.getImageData(0, 0, W, H);
    const pixels = imageData.data;
    let transparent = 0;
    const total = pixels.length / 4;
    
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparent++;
    }

    const pct = (transparent / total) * 100;
    setScratchedPercentage(pct);

    if (pct >= scratchThreshold && !revealingRef.current) {
      revealingRef.current = true
      revealResult();
    }
  };

  const revealResult = () => {
    if (revealingRef.current === false) {
    // (rare race) ensure we’re the first call
      revealingRef.current = true;
    }
    setIsGameActive(false);
    
    const scratchCanvas = scratchCanvasRef.current;
    const ctx = scratchCanvas?.getContext('2d');
    if (scratchCanvas && ctx) {
      ctx.clearRect(0, 0, scratchCanvas.width, scratchCanvas.height);
    }

    const selectedPrize = prizeNumber >= 0 ? prizesState[prizeNumber] : null;
    if (!selectedPrize) return; // guard

    const oldStock = selectedPrize.stock ?? 0;
    
    // Confetti efekti (kazanırsa)
    if (selectedPrize.isPrize) {
      createConfetti();
    }

    Swal.fire({
      title: selectedPrize.prizeValue,
      text: selectedPrize.isPrize
        ? (modalConfig.modalText ?? `Tebrikler! Hediye kazandınız ${collectUserData === 3 ? 'Hediyenizi almak için kayıt oluşturun.' : ''}`)
        : (modalConfig.modalPassText ?? 'Katıldığınız için teşekkürler.'),
      icon: selectedPrize.isPrize ? 'success' : 'error',
      imageUrl: selectedPrize.prizeImage ? (selectedPrize.prizeImage.startsWith('http') ? selectedPrize.prizeImage : env + selectedPrize.prizeImage) : undefined,
      imageWidth: 200,
      showCancelButton: collectUserData === 3 ? true : false,
      showConfirmButton: collectUserData === 3 ? true : false,
      confirmButtonText: 'Kullanıcı Formu',
      cancelButtonText: 'Başa Dön',
      customClass: {
        popup: modalConfig.modalBg ? 'swal-container swal-bg d-bg-hide' : `swal-container ${modalConfig.modalBorderRadius === false ? 'no-radius' : ''}`,
      },
      background: modalConfig.modalBg ? `url(${modalConfig.modalBg})`
        : (modalConfig.modalInnerBg ? `url(${modalConfig.modalInnerBg})` : (modalConfig.modalColor ?? '#fff')),
      color: modalConfig.modalTextColor ?? '#545454',
      didOpen: () => {
        if (collectUserData !== 1) {
          document.addEventListener('click', () => {
            Swal.close();
          }, { once: true });
        }
      },
    }).then(async (result) => {
      if (submittingRef.current) return;  // prevent double save
      submittingRef.current = true;

      let playerId = uuid();
      const gameData: any = {
        id: uuid(),
        prize: selectedPrize.prizeValue,
        isPrize: selectedPrize.isPrize,
        hasWon: !!selectedPrize.isPrize,
        datePlayed: new Date().toISOString(),
        campaignId: campaignId,
        playerId: playerId,
        player: buildPlayerData(formValues, formState, campaignId, playerId),
      };

      if (collectUserData === 2 && player != null) {
        gameData.player = player;
        gameData.playerId = player.id;
      }

      if (selectedPrize.isPrize && oldStock > 0) {
        const updated = {
          ...selectedPrize,
          stock: oldStock! - 1,
          scratchWinId: scratchWin.id,
        };
        setPrizesState(prev =>
          prev.map(p => p.id === updated.id ? { ...p, stock: updated.stock } : p)
        );

        await agent.ScratchPrizes.update(updated);
        onStockDepletion();

        if (updated.stock <= 0) {
          awaitingRefreshRef.current = true;
        }
      }

      setPlayer(gameData.player);
      await agent.Games.create(gameData);

      if (isActive && collectUserData === 3 && result.isConfirmed) {
        setOpenModal(true);
      } else {
        // reset for next round
        setPlayer(null);
        setDataCollected(false);
        if (!awaitingRefreshRef.current) {
          void newGame();
        }
      }
    });
  };

  const createConfetti = () => {
    const colors = ['#f1c40f', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6'];
    const gameContainer = gameContainerRef.current;
    
    if (!gameContainer) return;
    
    for (let i = 0; i < 50; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.style.position = 'absolute';
        confetti.style.width = '10px';
        confetti.style.height = '10px';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.top = '-10px';
        confetti.style.borderRadius = '50%';
        confetti.style.pointerEvents = 'none';
        confetti.style.zIndex = '1000';
        
        gameContainer.style.position = 'relative';
        gameContainer.appendChild(confetti);
        
        // Animasyon
        confetti.animate([
          { transform: 'translateY(-10px) rotate(0deg)', opacity: 1 },
          { transform: 'translateY(400px) rotate(360deg)', opacity: 0 }
        ], {
          duration: 3000,
          easing: 'ease-out'
        }).onfinish = () => confetti.remove();
      }, i * 50);
    }
  };

  const newGame = async () => {
    // clear canvases
    const scratchCanvas = scratchCanvasRef.current;
    const resultCanvas = resultCanvasRef.current;
    scratchCanvas?.getContext('2d')?.clearRect(0, 0, scratchCanvas.width, scratchCanvas.height);
    resultCanvas?.getContext('2d')?.clearRect(0, 0, resultCanvas.width, resultCanvas.height);

    // Konfetileri temizle
    const gameContainer = gameContainerRef.current;
    if (gameContainer) {
      const confettis = gameContainer.querySelectorAll('div[style*="position: absolute"]');
      confettis.forEach(confetti => confetti.remove());
    }
    
    // Oyunu yeniden başlat
    await initGame();
  };

  // Event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isGameActive) return;
    
    // const scratchCanvas = scratchCanvasRef.current;
    // if (!scratchCanvas) return;

    const move = (ev: MouseEvent) => scratch(ev);
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    scratch(e.nativeEvent);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!isGameActive) return;
    
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousedown', { clientX: touch.clientX, clientY: touch.clientY });
    scratch(mouseEvent);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!isGameActive) return;
    const t = e.touches[0];
    const me = new MouseEvent('mousemove', { clientX: t.clientX, clientY: t.clientY });
    scratch(me);
  };

  // Component mount olduğunda oyunu başlat
  useEffect(() => {
    void initGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    if (collectUserData != 2 && !dataCollected) {
      setPlayer(null);
    }
    setOpenModal(false);
    setFormValues({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      company: "",
      gender: 1,
      formAddPermissionChecked: false,
      formAddPermission2Checked: false,
    });
    void newGame();
  };

  const buildPlayerData = (formValues: any, formState: FormStateProps['formState'], campaignId: string, playerId: string) => {
    return {
      id: playerId,
      campaignId,
      firstName: formValues.firstName || "",
      lastName: formValues.lastName || "",
      gender: formState.formGender ? formValues.gender === 1 ? 1 : 2 : 0,
      email: formState.formEmail ? formValues.email || "" : "",
      phone: formState.formPhone ? formValues.phone || "" : "",
      company: formState.formCompany ? formValues.company || "" : "",
      formAddPermissionChecked: formState.formAddPermission ? formValues.formAddPermissionChecked || false : false,
      formAddPermission2Checked: formState.formAddPermission2 ? formValues.formAddPermission2Checked || false : false,
      bonusField: formState.bonusField ? formValues[formState.bonusField] || "" : "",
      bonusFieldLabel: collectUserData != 1 ? formState.bonusField || "" : "",
    };
  };

  const hasDuplicatePlayer = async (campaignId: string, values: UserDataValues) => {
    if (!formState.duplicateCheck) return false;
    const res = await agent.Games.exists(campaignId, {
      firstName: values.firstName,
      lastName: values.lastName,
      email: formState.formEmail ? values.email : undefined,
      phone: formState.formPhone ? values.phone : undefined,
    });
    return res.duplicate;
  };

  const handleSubmit = async (values: UserDataValues) => {
    if (await hasDuplicatePlayer(campaignId, values)) {
      toast.error('Aynı oyuncu bir kampanyada sadece bir kez oynayabilir.');
      return false;
    }

    const pid = player?.id || uuid();
    const pdata = buildPlayerData(values, formState, campaignId, pid);

    if (collectUserData === 2) {
      setPlayer(pdata);
      setDataCollected(true);
      setOpenModal(false);
      // start actual game now
      await initGame();
    } else {
      await agent.Players.update(pdata);
    }
    return true;
  };

  return (
    <div
      className="game-container"
      ref={gameContainerRef}
      style={{ color: textColor }}
    >
      {logoUrl && (
        <img
          className="screen-logo scratch-screen-logo"
          src={logoUrl}
          alt={titleText}
        />
      )}
      <h1 className="title">{titleText}</h1>
      <p className="instructions">{mottoText}</p>
      
      <div
        className="scratch-area"
        style={coverImageUrl ? { backgroundImage: `url(${coverImageUrl})` } : undefined}
      >
        <canvas 
          ref={resultCanvasRef}
          className="result-canvas" 
          width="400" 
          height="200"
        />
        <canvas
          ref={scratchCanvasRef}
          className="scratch-canvas"
          width="400"
          height="200"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        />
      </div>
      
      <p className="scratch-instruction">{isGameActive ? 'Alanı kazıyın!' : 'Yeni oyun için tıklayın.'}</p>
      <button className="new-game-btn" onClick={newGame}>Yeni Oyun</button>

      <UserDataModal
        open={!!(isActive && collectUserData === 2 && !dataCollected) || openModal}
        onClose={handleClose}
        formState={formState}
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        submitLabel="Gönder"
      />
    </div>
  );
};

export default ScratchAndWin;
